<template>
  <div class="c-config-content">
    <h4 class="c-config-content__title">{{ $t('queue') }}</h4>
    <div>
      <div class="c-template-table table-responsive c-role-table">
        <table class="table table-hover">
          <thead>
          <tr style="background-color: #F1F5FB">
            <th>{{ $t('queueName') }}</th>
            <th>{{ $t('ext') }}</th>
            <th class="th-80 text-right">
              <span @click="create" class="btn-add"><i class="icon16-plus-circle-gadient"></i></span>
            </th>
          </tr>
          </thead>
          <tbody v-if="items.length">
          <tr v-for="item in items" :key="item._id">
            <td>{{ item.name }}</td>
            <td>{{ item.ext }}</td>
            <td class="text-right">
              <button @click="update(item._id)" class="btn btn-outline-gray btn-sm"
                      type="button">
                <font-awesome-icon icon="info"/>
              </button>
            </td>
          </tr>
          </tbody>
          <tbody v-else>
          <tr>
            <td colspan="3" class="text-center text-gray-999">{{ $t('noQueueFound') }}</td>
          </tr>
          </tbody>
        </table>
      </div>
      <div class="c-paging" v-if="itemCount > filterFormModel.pageSize">
        <b-pagination size="sm" class="pagination pagination-sm justify-content-center"
                      v-model="filterFormModel.page" :total-rows="itemCount"
                      :per-page="filterFormModel.pageSize" @input="load">
        </b-pagination>
      </div>
      <b-modal size="lg" v-model="showForm" :title="$t('queueFormTitle')" @ok="save" no-close-on-backdrop
               no-enforce-focus>
        <div class="row">
          <div class="col-md">
            <form-input :label="$t('queueName')" :model="formModel" attribute="name" :errors="formErrors" :placeholder="$t('queueNamePlaceholder')"></form-input>
            <form-input :label="$t('ext')" :model="formModel" attribute="ext" :errors="formErrors" :placeholder="$t('queueExtPlaceholder')"></form-input>
            <form-input :label="$t('timeout')" :model="formModel" attribute="timeout" :errors="formErrors"></form-input>
            <form-select :label="$t('ringStrategy')" :model="formModel" attribute="ringStrategy" :errors="formErrors" :options="ringStrategyOptions"></form-select>
            <form-input :label="$t('agentTimeout')" :model="formModel" attribute="agentTimeout" :errors="formErrors"></form-input>
          </div>
          <div class="col-md">
            <form-input :label="$t('delayTime')" :model="formModel" attribute="delayTime" :errors="formErrors"></form-input>
            <form-input :label="$t('maxNoAnswer')" :model="formModel" attribute="maxNoAnswer" :errors="formErrors"></form-input>
            <recording-select :label="$t('queueGreetRecording')" :model="formModel" attribute="greetRecording" :errors="formErrors"></recording-select>
            <recording-select :label="$t('timeoutRecording')" :model="formModel" attribute="timeoutRecording" :errors="formErrors"></recording-select>
            <div v-if=formModel.timeout>
              <target-select :label="$t('execOnTimeout')" :model="formModel" attribute="execOnTimeout" :errors="formErrors"></target-select>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-md">
            <client-user-tree-select multiple :client-filter="client._id" :label="$t('agent')" :model="formModel" attribute="agents" :errors="formErrors"/>
          </div>
        </div>
        <template v-slot:modal-footer>
          <b-button variant="secondary" class="float-right" @click="showForm = false">{{ $t('close') }}
          </b-button>
        </template>
      </b-modal>
    </div>
  </div>
</template>

<script>
import list from '@/mixins/List';
import crud from '@/mixins/CRUD';
import RecordingSelect from "@/components/ClientRecordingSelect.vue";
import TargetSelect from "@/components/ClientTargetSelect.vue";
import ClientUserTreeSelect from "@/components/ClientUserTreeSelect.vue";

export default {
  props: ['client'],
  mixins: [list, crud],
  components: {RecordingSelect, TargetSelect, ClientUserTreeSelect},
  data: function () {
    return {
      service: '/queue',
      autoLoad: true,
      defaultFilterFormModel: {
        client: this.client._id
      },
      ringStrategyOptions: [
        {value: 'longest-idle-agent', text: this.$t('longestIdleAgent')},
        {value: 'agent-with-least-talk-time', text: this.$t('agentWithLeastTalkTime')},
        {value: 'agent-with-fewest-calls', text: this.$t('agentWithFewestCalls')},
        {value: 'top-down', text: this.$t('topDown')},
        {value: 'round-robin', text: this.$t('roundRobin')},
        {value: 'random', text: this.$t('random')},
        {value: 'ring-all', text: this.$t('ringAll')},
      ],
    }
  },
  i18n: {
    messages: {
      vi: {
        queue: "Hàng đợi",
        queueFormTitle: "Thông tin hàng đợi",
        queueName: "Tên hàng đợi",
        timeout: "Thời gian chờ tối đa (giây)",
        ringStrategy: "Phân phối cuộc gọi",
        agentTimeout: "Thời gian đổ chuông mỗi nhân viên (giây)",
        delayTime: "Thời gian nghỉ giữa các cuộc gọi",
        maxNoAnswer: "Số lần nhân viên không nghe máy tối đa trước khi bị loại",
        queueGreetRecording: "File ghi âm khi khách đang chờ",
        timeoutRecording: "File ghi âm khi hết thời gian chờ mà không có hành động kế tiếp",
        execOnTimeout: "Hành động khi hết thời gian chờ (dừng cuộc gọi nếu không chọn)",
        ringAll: "Đổ chuông toàn bộ",
        random: "Ngẫu nhiên",
        roundRobin: "Chia đều lần lượt",
        topDown: "Ưu tiên theo thứ tự nhân viên",
        agentWithFewestCalls: "Nhân viên có ít cuộc gọi nhất",
        agentWithLeastTalkTime: "Nhân viên có thời gian nói ít nhất",
        longestIdleAgent: "Nhân viên nghỉ lâu nhất",
        close: "Đóng",
        noQueueFound: "Chưa có hàng đợi nào được thiết lập",
      },
      en: {
        queue: "Hàng đợi",
        queueFormTitle: "Thông tin hàng đợi",
        queueName: "Tên hàng đợi",
        timeout: "Thời gian chờ tối đa (giây)",
        ringStrategy: "Phân phối cuộc gọi",
        agentTimeout: "Thời gian đổ chuông mỗi nhân viên (giây)",
        delayTime: "Thời gian nghỉ giữa các cuộc gọi",
        maxNoAnswer: "Số lần nhân viên không nghe máy tối đa trước khi bị loại",
        queueGreetRecording: "File ghi âm khi khách đang chờ",
        timeoutRecording: "File ghi âm khi hết thời gian chờ mà không có hành động kế tiếp",
        execOnTimeout: "Hành động khi hết thời gian chờ (dừng cuộc gọi nếu không chọn)",
        ringAll: "Đổ chuông toàn bộ",
        random: "Ngẫu nhiên",
        roundRobin: "Chia đều lần lượt",
        topDown: "Ưu tiên theo thứ tự nhân viên",
        agentWithFewestCalls: "Nhân viên có ít cuộc gọi nhất",
        agentWithLeastTalkTime: "Nhân viên có thời gian nói ít nhất",
        longestIdleAgent: "Nhân viên nghỉ lâu nhất",
        close: "Đóng",
        noQueueFound: "Chưa có hàng đợi nào được thiết lập",
      }
    }
  }
}
</script>